import { useState } from 'react';
import { trpc } from '../api/trpc';
import { ConfirmDialog } from './ConfirmDialog';

interface Props {
  connectorId: string;
  /** Connector label, used in the confirmation prompt. */
  connectorLabel: string;
  /** Called after the connector is deleted — host should close the modal and refresh. */
  onDeleted: () => void;
}

// Quiet, de-emphasised "remove connector" control for the connector Details
// modal. Deliberately understated — a muted text link that turns red only on
// hover — so the settings UI never invites accidental deletion. Removal always
// goes through a confirmation dialog.
export function ConnectorDeleteControl({ connectorId, connectorLabel, onDeleted }: Props) {
  const [confirming, setConfirming] = useState(false);
  const deleteMut = trpc.connectors.delete.useMutation();

  async function handleDelete() {
    try {
      await deleteMut.mutateAsync({ connectorId });
      onDeleted();
    } catch {
      // Delete failed — the connector stays; the dialog is already closed.
    }
  }

  return (
    <div className="connector-remove-row">
      <button
        type="button"
        className="connector-remove-link"
        onClick={() => setConfirming(true)}
        disabled={deleteMut.isPending}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="3 6 5 6 21 6" />
          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
        </svg>
        Remove this connector
      </button>
      {confirming && (
        <ConfirmDialog
          title="Remove this connector?"
          message={`Cade will lose access to ${connectorLabel} and will stop querying its data. You can reconnect it later.`}
          confirmLabel="Remove"
          onConfirm={() => { setConfirming(false); void handleDelete(); }}
          onCancel={() => setConfirming(false)}
        />
      )}
    </div>
  );
}
